import {createPillow} from '../design/configurator/room/soft-bedding';
import * as T from 'three';
import {RoundedBoxGeometry} from 'three/addons/geometries/RoundedBoxGeometry.js';
import {fabricTexture} from '../design/configurator/room/soft-bedding';

// Concept model in feet, using full-size Twin sleeping alcoves.
export function createCastleModel(){
 const root=new T.Group();root.name='castle-concept';
 const plaster=new T.MeshStandardMaterial({color:0xfaf9f6,roughness:.65});
 const trim=new T.MeshStandardMaterial({color:0xffffff,roughness:.4});
 const linen=new T.MeshStandardMaterial({color:0xfffaf0,roughness:1,map:fabricTexture()});
 const roof=new T.MeshStandardMaterial({color:0xfafafa,roughness:.5});
 const brass=new T.MeshStandardMaterial({color:0xa68b55,roughness:.35,metalness:.65});
 const glow=new T.MeshStandardMaterial({color:0xffefd2,emissive:0xffcf8b,emissiveIntensity:.8,roughness:.7});
 const box=(name:string,w:number,h:number,d:number,x:number,y:number,z:number,mat=plaster)=>{
  const soft=name==='mattress'||name==='pillow';
  const radius=Math.min(soft?.12:.015,Math.min(w,h,d)*.4);
  const m=new T.Mesh(new RoundedBoxGeometry(w,h,d,soft?4:2,radius),mat);m.name=name;m.position.set(x,y,z);m.castShadow=true;m.receiveShadow=true;root.add(m);return m;
 };
 const bookColors=[0x698080,0xb19b78,0xeee4d1,0x7d665a,0x879475].map(color=>new T.MeshStandardMaterial({color,roughness:.9}));
 // Open toward -X. All shelf edges and books remain behind the wall face.
 const shelfNiche=(face:number,bottom:number,z:number,width=1.55)=>{
  const depth=.32;
  box('recessed-shelf-back',.04,1.05,width,face+depth,bottom+.525,z,plaster);
  for(const y of [bottom,bottom+1.05])box('built-in-shelf',depth,.045,width,face+depth/2,y,z,trim);
  for(const edge of [-1,1])box('shelf-reveal',depth,1.05,.035,face+depth/2,bottom+.525,z+edge*width/2,trim);
  for(let i=0;i<7;i++){
   const h=.58+(i%3)*.1,spine=z-width/2+.16+i*.18;
   box('shelf-book',.24,h,.13,face+.19,bottom+.04+h/2,spine,bookColors[i%bookColors.length]);
   box('book-spine-band',.012,.025,.105,face+.064,bottom+.18,spine,linen);
  }
 };
 const sconce=(x:number,y:number,z:number)=>{
  box('sconce-backplate',.2,.48,.07,x,y+1.04,z,brass);
  box('sconce-arm',.07,.07,.32,x,y+.95,z+.18,brass);
  const shade=new T.Mesh(new T.CylinderGeometry(.17,.25,.4,24),glow);
  shade.name='compartment-sconce';shade.position.set(x,y+1.15,z+.34);root.add(shade);
  const light=new T.SpotLight(0xffdfb3,.8,2.4,Math.PI/4,1,2);
  light.name='sconce-downlight';light.position.set(x,y+.93,z+.4);
  light.target.position.set(x,y-.8,z+.08);root.add(light,light.target);
 };
 // Four adjacent alcoves, with a turret at the left and stairs at the right.
 for(const x of [-3.35,3.35]){
  box('alcove-back',6.7,8.1,.18,x,4.05,-1.9);
  for(const y of [.65,4.5]){
   box('bed-platform',6.5,.38,3.8,x,y,0);
   box('mattress',6.25,.38,3.5,x,y+.38,0,linen);
   const sleepingPillow=createPillow(20,26,5);sleepingPillow.name='pillow';sleepingPillow.position.set(x-2.05,y+.78,0);root.add(sleepingPillow);
   sconce(x-2.1,y+1.1,-1.76);
   // Pillow is at the left/head end; shelf is on the opposite foot wall.
   shelfNiche(x<0?-.3:6.35,y+1.1,0);
   for(const edge of [-3.3,3.3])box('arch-pier',.22,3.4,.3,x+edge,y+1.65,1.9);
   const shape=new T.Shape();const r=3.2,top=3.4,spring=1.45;
   shape.moveTo(-r,top);shape.lineTo(r,top);shape.lineTo(r,spring);
   shape.absellipse(0,spring,r,1.8,0,Math.PI,false,0);shape.lineTo(-r,top);
   const arch=new T.Mesh(new T.ExtrudeGeometry(shape,{depth:.25,bevelEnabled:false,curveSegments:32}),plaster);
   arch.name='alcove-arch';arch.position.set(x,y,1.8);root.add(arch);
   // Raised white architrave follows the opening, with a fine outer bead.
   for(const [offset,radius] of [[0,.065],[.14,.025]]){
    const points:T.Vector3[]=[];
    points.push(new T.Vector3(x+3.2+offset,y,2.08));
    for(let i=0;i<=48;i++){
     const a=i/48*Math.PI;
     points.push(new T.Vector3(x+(3.2+offset)*Math.cos(a),y+1.45+(1.8+offset)*Math.sin(a),2.08));
    }
    points.push(new T.Vector3(x-3.2-offset,y,2.08));
    const path=new T.CurvePath<T.Vector3>();
    for(let i=1;i<points.length;i++)path.add(new T.LineCurve3(points[i-1],points[i]));
    const molding=new T.Mesh(new T.TubeGeometry(path,96,radius,8,false),trim);
    molding.name='raised-alcove-architrave';root.add(molding);
   }

  }
 }
 for(let i=0;i<7;i++)box('central-stair',2.1,(i+1)*.64,.78,7.8,(i+1)*.32,8-i*.78,trim);
 // Hollow reading turret: curved shell, an open arch and a usable daybed nook.
 {
  const x=-8.05,z=.5,r=1.5;
  // A cylindrical wall with a curved arch cut out of its front face.
  const vertices:number[]=[];
  const openingAngle=.92;
  const lower=(angle:number)=>{
   const wrapped=Math.atan2(Math.sin(angle),Math.cos(angle));
   if(Math.abs(wrapped)>=openingAngle)return 0;
   const t=Math.sin(wrapped)/Math.sin(openingAngle);
   return 4.5+1.25*Math.sqrt(Math.max(0,1-t*t));
  };
  for(let i=0;i<128;i++){
   const a=i/128*Math.PI*2,b=(i+1)/128*Math.PI*2;
   const point=(angle:number,y:number)=>[r*Math.sin(angle),y,r*Math.cos(angle)];
   const p=point(a,lower(a)),q=point(b,lower(b)),u=point(a,7.9),v=point(b,7.9);
   // Recessed book niche on the right side of the cylindrical nook.
   if(a>=Math.PI/2-.55&&b<=Math.PI/2+.55){
    const loA=point(a,1.9),loB=point(b,1.9),hiA=point(a,2.95),hiB=point(b,2.95);
    vertices.push(...p,...q,...loA,...q,...loB,...loA,...hiA,...hiB,...u,...hiB,...v,...u);
   }else vertices.push(...p,...q,...u,...q,...v,...u);
  }
  const geometry=new T.BufferGeometry();
  geometry.setAttribute('position',new T.Float32BufferAttribute(vertices,3));geometry.computeVertexNormals();
  const shell=new T.Mesh(geometry,new T.MeshStandardMaterial({color:0xfaf9f6,roughness:.65,side:T.DoubleSide}));
  shell.name='cylindrical-turret-with-open-arch';shell.position.set(x,0,z);root.add(shell);
  // The curved rear wall is visible from inside the nook as well as outside.
  const innerGeometry=geometry.clone();innerGeometry.scale((r-.14)/r,1,(r-.14)/r);
  const innerWall=new T.Mesh(innerGeometry,new T.MeshStandardMaterial({color:0xfaf9f6,roughness:.65,side:T.BackSide}));
  innerWall.name='turret-enclosed-curved-inner-wall';innerWall.position.set(x,0,z);root.add(innerWall);
  for(const y of [.09,7.8]){
   const disk=new T.Mesh(new T.CylinderGeometry(r-.07,r-.07,.18,64),plaster);
   disk.name=y<1?'turret-floor':'turret-ceiling';disk.position.set(x,y,z);root.add(disk);
  }
  // The daybed follows the cylindrical enclosure; no square corners project
  // through the curved wall or across the entrance.
  for(const [name,radius,height,y,material] of [
   ['turret-daybed-base',r-.16,.65,.325,trim],
   ['turret-daybed-cushion',r-.21,.28,.79,linen],
  ] as const){
   const seat=new T.Mesh(new T.CylinderGeometry(radius,radius,height,96),material);
   seat.name=name;seat.position.set(x,y,z);seat.castShadow=true;seat.receiveShadow=true;root.add(seat);
  }
  box('turret-daybed-back-cushion',2.15,1,.27,x,1.37,z-.8,linen);
  const cushion=createPillow(12,12,4);cushion.name='turret-daybed-pillow';cushion.rotation.x=Math.PI/2;cushion.position.set(x-.55,1.28,z-.52);root.add(cushion);
  sconce(x,1.9,z-1.27);
  shelfNiche(x+1.35,1.9,z,1.4);
  const cap=new T.Mesh(new T.ConeGeometry(1.63,2.05,48),roof);
  cap.name='turret-roof';cap.position.set(x,8.95,z);root.add(cap);
  const cornice=new T.Mesh(new T.CylinderGeometry(1.5,1.5,.18,48),trim);
  cornice.name='turret-cornice';cornice.position.set(x,7.95,z);root.add(cornice);
  for(const [y,radius,height] of [[.18,1.55,.2],[7.65,1.55,.12],[7.86,1.6,.12]]){
   const band=new T.Mesh(new T.CylinderGeometry(radius,radius,height,64),trim);
   band.name='turret-layered-molding';band.position.set(x,y,z);root.add(band);
  }
 }
 box('castle-cornice',13.4,.22,.42,0,8.1,1.9,trim);
 // Upper floor continues into a front gallery and a landing at the stair head.
 box('upper-gallery',13.4,.22,1.65,0,4.5,2.65,trim);
 box('stair-landing',2.1,.22,5.4,7.8,4.5,.8,trim);
 // Enclosed landing tower runs from the stair head to the rear wall.
 // Its deck is continuous with the front gallery at the same elevation.
 box('landing-tower',2.1,4.39,4.83,7.8,2.195,.515);
 // Full-height compartment walls also isolate the turret from both bunks.
 // Segment the foot walls around the niches, retaining a closed backing.
 for(const face of [-.3,6.35]){
  const depth=.4,cx=face+depth/2;
  // A continuous outer skin hides the niche box and meets the enclosure edge flush.
  box('smooth-foot-wall-exterior',.08,8,3.8,face+.36,4,0);
  for(const z of [-1.3375,1.3375])box('foot-wall-side',depth,8,1.125,cx,4,z);
  let floor=0;
  for(const bottom of [1.75,5.6]){
   box('foot-wall-between-niches',depth,bottom-floor,1.55,cx,(bottom+floor)/2,0);
   floor=bottom+1.05;
  }
  box('foot-wall-above-niche',depth,8-floor,1.55,cx,(8+floor)/2,0);
 }
 box('turret-bunk-divider',.2,8,3.8,-6.65,4,0);
 for(const x of [-3.35,3.35]){
  box('lower-bunk-plinth',6.5,.46,3.8,x,.23,0);
  box('upper-compartment-ceiling',6.7,.24,4.05,x,8.02,.075);
  box('upper-front-header',6.7,.22,.3,x,7.94,1.9);
 }
 // Columns carry the gallery to the floor, aligned with the alcove dividers.
 for(const x of [-6.65,0,6.65]){
  box('walkway-support-pillar',.28,4.39,.28,x,2.195,3.32);
  box('pillar-foot',.4,.18,.4,x,.09,3.32,trim);
  box('pillar-capital',.42,.18,.42,x,4.3,3.32,trim);
 }
 // Close the stair-side gap beneath the landing; stairs meet this enclosure.
 box('stair-enclosure-return',.2,4.39,1.6,6.65,2.195,2.7);
 box('gallery-handrail',13.4,.14,.14,0,6.0,3.42,trim);
 box('gallery-bottom-rail',13.4,.12,.12,0,4.72,3.42,trim);
 for(let x=-6.65;x<=6.65;x+=.38)box('gallery-baluster',.065,1.28,.065,x,5.36,3.42,trim);
 for(const x of [-6.65,0,6.65])box('gallery-newel',.15,1.65,.15,x,5.3,3.42,trim);
 // Close the exposed gallery end back to the enclosure; the stair end stays open for access.
 box('gallery-handrail-return',.14,.14,1.52,-6.65,6,2.66,trim);
 box('gallery-bottom-rail-return',.12,.12,1.52,-6.65,4.72,2.66,trim);
 for(let z=2.09;z<3.42;z+=.38)box('gallery-return-baluster',.065,1.28,.065,-6.65,5.36,z,trim);

 // Stair balustrade follows the rising treads beside the bunk enclosure.
 for(let i=0;i<7;i++){box('stair-baluster',.08,1.5,.08,6.75,(i+1)*.64+.75,8-i*.78,trim);}
 const rail=box('stair-handrail',.13,.13,6.2,6.75,4.06,5.66,trim);rail.rotation.x=Math.atan2(3.84,4.68);
 for(let x=-6.45;x<=6.45;x+=.62)box('battlement',.42,.48,.4,x,8.4,1.9);
 // Layered fascia, plinths and pilaster detail create depth through shadow, all in white.
 for(const [y,h,d] of [[4.27,.12,.12],[4.43,.09,.18],[7.88,.09,.16],[8.13,.1,.22]])
  box('layered-front-molding',13.45,h,d,0,y,2.12,trim);
 for(const x of [-6.65,0,6.65]){
  box('pilaster-face',.3,3.45,.08,x,2.18,2.09,trim);
  box('pilaster-cap',.42,.14,.18,x,3.85,2.1,trim);
 }
 for(const x of [-3.35,3.35]){
  for(const y of [.12,.49])box('plinth-edge-molding',6.45,.07,.08,x,y,1.94,trim);
  for(const dx of [-2.1,0,2.1]){
   for(const y of [.2,.41])box('plinth-panel-horizontal',1.85,.035,.05,x+dx,y,1.95,trim);
   for(const edge of [-.925,.925])box('plinth-panel-vertical',.035,.21,.05,x+dx+edge,.305,1.95,trim);
  }
 }
 // Matching paneled skirts on both sides, including the face toward the bunks.
 const skirtShape=new T.Shape();skirtShape.moveTo(2.93,0);skirtShape.lineTo(8.4,0);
 skirtShape.lineTo(8.4,.64);skirtShape.lineTo(2.93,4.48);skirtShape.closePath();
 for(const [panelX,trimX] of [[8.92,8.96],[6.75,6.6]]){
 const skirt=new T.Mesh(new T.ExtrudeGeometry(skirtShape,{depth:.12,bevelEnabled:false}),plaster);
 skirt.name='white-stair-side-panel';skirt.rotation.y=-Math.PI/2;skirt.position.x=panelX;root.add(skirt);
 const slope=box('stair-side-sloped-molding',.07,.12,6.68,trimX,2.56,5.665,trim);
 slope.rotation.x=Math.atan2(3.84,5.47);
 box('stair-skirt-base-molding',.08,.14,5.47,trimX,.12,5.665,trim);
 for(const z of [3.65,4.85,6.05,7.25,8.025]){
  const rise=3.84/5.47;
  const h=4.48-(z-2.93)*rise-.2;
  const width=z>7.9?.35:.9;
  box('stair-panel-bottom',.07,.055,width,trimX,.35,z,trim);
  const panelTop=box('stair-panel-sloped-top',.07,.055,Math.hypot(width,width*rise),trimX,h,z,trim);
  panelTop.rotation.x=Math.atan(rise);
  for(const edge of [-width/2,width/2]){
   const top=h-edge*rise;
   box('stair-panel-vertical',.07,top-.35,.055,trimX,(top+.35)/2,z+edge,trim);
  }
 }
 }
 for(const z of [-1.1,.5,2.1]){
  for(const y of [.35,3.95])box('landing-panel-horizontal',.07,.065,1.3,8.88,y,z,trim);
  for(const edge of [-.65,.65])box('landing-panel-vertical',.07,3.6,.065,8.88,2.15,z+edge,trim);
 }
 root.traverse(object=>{
  if(object instanceof T.Mesh){object.castShadow=true;object.receiveShadow=true;}
  if(object instanceof T.SpotLight){object.castShadow=true;object.shadow.mapSize.set(512,512);object.shadow.bias=-.0001;object.shadow.normalBias=.01;}
 });
 return root;
}

